import React, { useState, useRef, useEffect } from 'react'
import styles from './Tab.module.css'
import { TabLabel, TabContainer, TabsList, ContainerIndicator, Container } from './Tab.styles'

export interface TabItemProps {
  label: string
  children: React.ReactNode
  dataset?: { [key: string]: any }
}

interface TabProps {
  tabs: TabItemProps[]
  color: string
  activeColor: string
  disabledColor?: string | null
  tabContainerClassName?: string
  onChangeActive?: (x: number) => void
}

export const Tab = ({ tabs, activeColor, color, onChangeActive, tabContainerClassName = '', disabledColor }: TabProps) => {
  const [active, setActive] = useState<number>(0)
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({})
  const tabsRef = useRef<(HTMLDivElement | null)[]>([])

  const updateIndicator = (index: number) => {
    const currentTab = tabsRef.current[index]
    if (currentTab) {
      setIndicatorStyle({
        height: '3px',
        width: currentTab.offsetWidth || '40px',
        transform: `translateX(${currentTab.offsetLeft}px)`,
        transition: 'transform 300ms ease, width 300ms ease',
      })
    }
  }

  useEffect(() => {
    updateIndicator(active)
    if(onChangeActive) onChangeActive(active)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  return (
    <Container>
      <TabsList className={`${styles.noScrollbar} ${tabContainerClassName}`}>
        {tabs.map((tab, index) => (
          <TabContainer
            key={index}
            ref={(el) => { 
              tabsRef.current[index] = el 
            }}
            onClick={() => {
              setActive(index)
              updateIndicator(index)
            }}
            {...(tab.dataset && Object.keys(tab.dataset).length > 0 ? Object.fromEntries(
              Object.entries(tab.dataset).map(([key, value]) => [`data-${key}`, value])
            ) : {})}
            data-testid='tab'
          >
            <TabLabel
              color={color}
              isActive={active === index}
              style={{ color: disabledColor && active !== index ? disabledColor : color, opacity: active === index ? '1' : '0.6' }}
              data-testid='tab-title'
            >{tab.label}</TabLabel>
          </TabContainer>
        ))}
        <ContainerIndicator
          color={activeColor}
          style={indicatorStyle}
        ></ContainerIndicator>
      </TabsList>

      {tabs[active].children}
    </Container>
  )
}
